import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { advancingCount, type GuestMe, type TournamentDetail } from '@brochure/shared'
import { buildRounds } from '@/lib/bracket'
import { SegmentedTabs } from '@/components/ui/SegmentedTabs'
import { MatchCard } from './MatchCard'
import { MatchList } from './MatchList'
import { StandingsTable } from './StandingsTable'
import { Bracket } from './Bracket'
import { ScorersList } from './ScorersList'
import { CoinsBoard } from './CoinsBoard'
import { CoinLedger } from './CoinLedger'
import { StatsPanel } from './StatsPanel'

const TABS = ['group', 'knockout', 'standings', 'scorers', 'coins', 'stats'] as const
type TabKey = (typeof TABS)[number]

type TournamentTabsProps = {
  detail: TournamentDetail
  me?: GuestMe
  tournamentId: string
  /** Contatore di messaggi live (da `useTournament`): fa rileggere gli eventi della tab statistiche. */
  version: number
}

/** Barra a colpo d'occhio, scorrevole su mobile, sulle squadre live per popolare la chip in classifica. */
function buildLiveByTeam(detail: TournamentDetail): Map<string, { myScore: number; oppScore: number }> {
  const liveByTeam = new Map<string, { myScore: number; oppScore: number }>()
  for (const match of detail.matches) {
    if (match.status !== 'live' || match.homeScore === null || match.awayScore === null) continue
    liveByTeam.set(match.homeTeamId, { myScore: match.homeScore, oppScore: match.awayScore })
    liveByTeam.set(match.awayTeamId, { myScore: match.awayScore, oppScore: match.homeScore })
  }
  return liveByTeam
}

/** Tab del torneo in corso o concluso: girone, eliminatorie (previste finché il girone non finisce), classifica,
 * cannonieri e monete. La tab statistiche è montata dalla Task 4. Stato della tab attiva nell'URL (`?tab=`). */
export function TournamentTabs({ detail, me, tournamentId, version }: TournamentTabsProps) {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const { tournament, teams, matches, standings, scorers, coins } = detail

  const groupMatches = matches.filter((m) => m.phase === 'group')
  // Senza girone (2 squadre: finale diretta) la tab girone è vuota: si parte dalle eliminatorie.
  const hasGroup = groupMatches.length > 0
  const isGroupOver = groupMatches.every((m) => m.status === 'completed')
  const defaultTab: TabKey = tournament.status === 'completed' ? 'standings' : hasGroup ? 'group' : 'knockout'
  const requested = searchParams.get('tab')
  const active: TabKey = (TABS as readonly string[]).includes(requested ?? '') ? (requested as TabKey) : defaultTab

  function selectTab(tab: TabKey) {
    const next = new URLSearchParams(searchParams)
    next.set('tab', tab)
    setSearchParams(next)
  }

  const liveByTeam = buildLiveByTeam(detail)
  const liveMatches = matches.filter((m) => m.status === 'live').sort((a, b) => a.sortOrder - b.sortOrder)
  const advancing = advancingCount(teams.length)
  const rounds = buildRounds(matches, advancing)

  return (
    <div>
      {liveMatches.length > 0 && (
        // "In campo ora": le partite live in cima, su tutte le tab, come le righe del tabellone partenze.
        <section aria-labelledby="bp-live-now" className="mb-6 rounded-2xl bg-ink p-3 text-paper shadow-[0_18px_40px_-18px_var(--color-ink)]">
          <h2 id="bp-live-now" className="mb-2.5 flex items-center gap-2 px-1 font-body text-xs font-semibold tracking-[0.02em] text-sun">
            <span className="size-1.5 rounded-full bg-sun motion-safe:animate-pulse" aria-hidden="true" />
            {t('zambePong.liveNow')}
          </h2>
          <ul className="grid grid-cols-1 gap-2">
            {liveMatches.map((match) => (
              <li key={match.id}>
                <MatchCard match={match} teams={teams} tournamentId={tournamentId} tone="night" withTestId={false} />
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Tab sticky sotto l'header del sito: la fascia carta copre il contenuto che scorre sotto. */}
      <div className="sticky top-16 z-20 -mx-4 bg-paper/95 px-4 py-2 backdrop-blur-md sm:-mx-6 sm:px-6 md:top-20 lg:-mx-8 lg:px-8">
        <SegmentedTabs
          tabs={TABS.map((tab) => ({ key: tab, label: t(`zambePong.tabs.${tab}`) }))}
          active={active}
          onSelect={selectTab}
          ariaLabel={t('zambePong.tabs.ariaLabel')}
          idPrefix="bp-tab"
          panelId="bp-tabpanel"
        />
      </div>

      <div className="mt-5" role="tabpanel" id="bp-tabpanel" aria-labelledby={`bp-tab-${active}`}>
        {active === 'group' && <MatchList matches={groupMatches} teams={teams} tournamentId={tournamentId} />}

        {active === 'standings' && (
          <div className="max-w-3xl">
            {/* Senza girone nessuno è eliminato prima di giocare: niente "OUT". */}
            <StandingsTable standings={standings} advancing={hasGroup ? advancing : standings.length} isGroupOver={isGroupOver} liveByTeam={liveByTeam} />
          </div>
        )}

        {active === 'knockout' && <Bracket rounds={rounds} teams={teams} tournamentId={tournamentId} />}

        {active === 'scorers' && <ScorersList scorers={scorers} teams={teams} />}

        {active === 'coins' && (
          <div>
            <CoinsBoard coins={coins} highlightId={me?.participant.id} />
            {me && (
              <div className="mt-8">
                <h3 className="font-display font-semibold text-h2 text-ink">{t('zambePong.coins.ledgerTitle')}</h3>
                <div className="mt-4">
                  <CoinLedger entries={me.entries} matches={matches} teams={teams} />
                </div>
              </div>
            )}
          </div>
        )}

        {active === 'stats' && <StatsPanel detail={detail} tournamentId={tournamentId} version={version} />}
      </div>
    </div>
  )
}
